import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth, connectAuthEmulator } from '@angular/fire/auth';
import { provideFirestore, getFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { provideStorage, getStorage, connectStorageEmulator } from '@angular/fire/storage';
import { provideFunctions, getFunctions, connectFunctionsEmulator } from '@angular/fire/functions';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { TenantConfig } from './core/config/tenant-config';
import { APP_CONFIG, AuthenticationService, BrandingService, ENVIRONMENT } from '@tamilarasitech/tt-branding-app-lib';
import { initializeTheme } from './core/theme/theme-initializer';
import { appIdInterceptor } from './core/interceptors/app-id.interceptor';
import { tenantIdInterceptor } from './core/interceptors/tenant-id.interceptor';

function svgLetterLogoDataUri(letter: string, bgColor: string, textColor: string): string {
  const safeLetter = (letter || 'S').toUpperCase().slice(0, 1);
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96">
  <rect width="96" height="96" rx="20" fill="${bgColor}"/>
  <text x="50%" y="54%" text-anchor="middle" dominant-baseline="middle"
        font-family="Inter, Roboto, Arial, sans-serif" font-size="44" font-weight="700" fill="${textColor}">
    ${safeLetter}
  </text>
</svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function applyBrandingFallbacks(config: any) {
  const defaultPrimary = '#1A237E'; // Deep Navy (from marketing-app)
  const defaultAccent = '#E67E22';  // Sunset Orange (from marketing-app)

  if (!config) {
    return config;
  }

  config.theme = config.theme ?? {};
  config.theme.mainColors = config.theme.mainColors ?? {};
  config.branding = config.branding ?? {};
  config.app = config.app ?? {};

  // Get colors from config (prefer theme.mainColors, then branding.colors, then branding.primaryColor)
  const mainColors = config.theme.mainColors;
  const primaryColor = 
    mainColors?.primaryColor ?? 
    config.branding?.colors?.primary ?? 
    config.branding?.primaryColor ?? 
    defaultPrimary;
  const accentColor = 
    mainColors?.accentColor ?? 
    config.branding?.colors?.accent ?? 
    config.branding?.secondaryColor ?? 
    defaultAccent;

  // Set theme colors (prefer existing, fallback to computed)
  mainColors.primaryColor = mainColors.primaryColor ?? primaryColor;
  mainColors.accentColor = mainColors.accentColor ?? accentColor;

  // Keep branding colors in sync (branding-lib reads these in multiple places)
  config.branding.primaryColor = primaryColor;
  config.branding.secondaryColor = accentColor;

  config.branding.colors = config.branding.colors ?? {};
  config.branding.colors.primary = config.branding.colors.primary ?? primaryColor;
  config.branding.colors.accent = config.branding.colors.accent ?? accentColor;

  // Set app name from tenantName if not already set
  if (!config.app.name && config.tenantName) {
    config.app.name = config.tenantName;
  }

  // Set branding name from tenantName if not already set
  if (!config.branding.name && config.tenantName) {
    config.branding.name = config.tenantName;
  }

  // Handle logo - prefer existing logo, only generate if missing or placeholder
  const currentLogo = config.app?.logo ?? config.branding?.logo;
  const isPlaceholderLogo =
    typeof currentLogo === 'string' &&
    (currentLogo.trim() === '' || currentLogo === '/assets/logo.png' || currentLogo === '/assets/images/logo.png');

  if (!currentLogo || isPlaceholderLogo) {
    const name = String(
      config.app?.name ?? config.tenantName ?? config.branding?.name ?? 'School'
    ).trim();
    const letter = name ? name[0] : 'S';
    const generated = svgLetterLogoDataUri(letter, primaryColor, '#FFFFFF');
    config.branding.logo = generated;
    config.app.logo = generated;
  } else {
    // Ensure logo is set in both places
    if (config.branding.logo && !config.app.logo) {
      config.app.logo = config.branding.logo;
    }
    if (config.app.logo && !config.branding.logo) {
      config.branding.logo = config.app.logo;
    }
  }

  return config;
}

function initializeBrandingConfig() {
  return () => {
    const brandingService = inject(BrandingService);
    
    // Get tenant config from window.__tenantConfig (set in main.ts before bootstrap)
    const tenantConfig = (window as any).__tenantConfig as TenantConfig | undefined;
    
    // Use tenant config if available, otherwise fall back to brandingService.config
    const sourceConfig = tenantConfig ? { ...tenantConfig } : { ...(brandingService.config as any) };
    const nextConfig = applyBrandingFallbacks(sourceConfig);

    // Tenant account config is already loaded in main.ts before bootstrap.
    // Keep initializer local-only so branding never triggers another startup API call.
    return brandingService.loadConfig(nextConfig as any).catch(() => true);
  };
}

/**
 * Get Firebase config from tenant config (set in main.ts) or fallback to environment
 */
function getFirebaseConfig(): any {
  const tenantConfig = (window as any).__tenantConfig as TenantConfig | undefined;
  const isBootstrapSafePage =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/error' || window.location.pathname === '/tenant');
  
  if (!tenantConfig) {
    if (!isBootstrapSafePage) {
      throw new Error('Tenant is not initialized');
    }
    return environment.firebase;
  }

  if (tenantConfig.firebaseConfig) {
    return tenantConfig.firebaseConfig;
  }

  return environment.firebase;
}

/**
 * Get tenant ID from tenant config or fallback to environment
 */
function getTenantId(): string {
  const tenantConfig = (window as any).__tenantConfig as TenantConfig | undefined;
  const isBootstrapSafePage =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/error' || window.location.pathname === '/tenant');

  if (tenantConfig?.tenantId) {
    return tenantConfig.tenantId;
  }

  if (!isBootstrapSafePage) {
    throw new Error('Tenant is not initialized');
  }

  return environment.tenantId || 'platform';
}

function getBrandingTenantId(): string {
  // Branding library expects a default tenant id to be used for tenant header/body.
  // This must be resolved at runtime, after main.ts sets window.__tenantConfig.
  return getTenantId();
}

/**
 * Get or initialize Firebase app
 */
let firebaseApp: any = null;
function getOrCreateFirebaseApp() {
  if (!firebaseApp) {
    firebaseApp = initializeApp(getFirebaseConfig());
  }
  return firebaseApp;
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ENVIRONMENT,
      useFactory: () => ({
        backendApi: {
          baseUrl: environment.baseApiUrl,
          authUrl: `/api/auth`,
        },
        defaultTenantId: getBrandingTenantId(),
        isMultiTenant: true,
      }),
    },
     {
      provide: APP_CONFIG,
      useFactory: () => ({
        basebaseAppUrl: environment.baseApiUrl,
        API_REGISTER_BUSINESS_ACCOUNT: `${environment.baseApiUrl}/api/business/register`,
        FS_BUSINESS_ACCOUNT_PATH: 'business_accounts',
        auth: {
          createUser: false,
        },
      }),
    },
    // Initialize dynamic theme system
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTheme,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeBrandingConfig,
      multi: true,
    },
    AuthenticationService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withFetch(), withInterceptors([tenantIdInterceptor, appIdInterceptor])),
    // Initialize Firebase with tenant-aware configuration
    provideFirebaseApp(() => getOrCreateFirebaseApp()),
    provideAuth(() => {
      const app = getOrCreateFirebaseApp();
      const auth = getAuth(app);
      if (environment.useEmulators) {
        try {
          connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
        } catch (e) {
          // Already connected
        }
      }
      return auth;
    }),
    provideFirestore(() => {
      const app = getOrCreateFirebaseApp();
      const firestore = getFirestore(app);
      if (environment.useEmulators) {
        try {
          connectFirestoreEmulator(firestore, 'localhost', 8080);
        } catch (e) {
          // Already connected
        }
      }
      return firestore;
    }),
    provideStorage(() => {
      const app = getOrCreateFirebaseApp();
      const storage = getStorage(app);
      if (environment.useEmulators) {
        try {
          connectStorageEmulator(storage, 'localhost', 9199);
        } catch (e) {
          // Already connected
        }
      }
      return storage;
    }),
    provideFunctions(() => {
      const app = getOrCreateFirebaseApp();
      const functions = getFunctions(app, 'asia-south1');
      if (environment.useEmulators) {
        try {
          connectFunctionsEmulator(functions, 'localhost', 5001);
        } catch (e) {
          // Already connected
        }
      }
      return functions;
    })
  ]
};
